<div class="dash-shell">
  <header class="page-head">
    <nav class="page-head__crumbs" aria-label="Breadcrumb">
      <a class="page-head__crumb" routerLink="/dashboard">Home</a>
      <span class="page-head__sep" aria-hidden="true">/</span>
      <a class="page-head__crumb" routerLink="/admin/dashboard">Admin</a>
      <span class="page-head__sep" aria-hidden="true">/</span>
      <span class="page-head__crumb page-head__crumb--current" aria-current="page"
        >Email approvals</span
      >
    </nav>
    <div class="page-head__bar">
      <div class="page-head__titles">
        <h1 class="page-head__title">Email approvals</h1>
        <p class="page-head__subtitle">
          Email to anyone who is not staff, agents included, waits here until you send it. Nobody is
          told it is waiting, so a person expecting a link stays stuck until it goes.
        </p>
      </div>
    </div>
  </header>

  <div class="queue-toolbar">
    <mat-button-toggle-group
      [value]="view()"
      (change)="setView($event.value)"
      aria-label="Which emails to show"
    >
      @for (v of views; track v.value) {
        <mat-button-toggle [value]="v.value" [attr.data-testid]="'queue-view-' + v.value">
          {{ v.label }}
          @if (counts(); as c) {
            ({{ c[v.value] | number }})
          }
        </mat-button-toggle>
      }
    </mat-button-toggle-group>

    <mat-form-field appearance="outline" class="queue-toolbar__search" subscriptSizing="dynamic">
      <mat-label>Search</mat-label>
      <app-icon name="search" [size]="18" matPrefix aria-hidden="true" />
      <input
        matInput
        type="search"
        data-testid="queue-search"
        [value]="search()"
        (input)="onSearch($any($event.target).value)"
        placeholder="Recipient or subject"
      />
    </mat-form-field>

    <mat-form-field appearance="outline" class="queue-toolbar__field" subscriptSizing="dynamic">
      <mat-label>Category</mat-label>
      <mat-select [value]="category()" (valueChange)="setCategory($event)">
        <mat-option value="">All categories</mat-option>
        @for (c of categories(); track c.value) {
          <mat-option [value]="c.value">{{ c.label }}</mat-option>
        }
      </mat-select>
    </mat-form-field>
  </div>

  @if (selected().size) {
    <div class="queue-bulk" role="region" aria-label="Selected emails">
      <span class="queue-bulk__count" aria-live="polite">{{ selected().size }} selected</span>
      @if (canSend()) {
        <button
          mat-flat-button
          type="button"
          data-testid="queue-bulk-send"
          (click)="approve(selectedIds())"
        >
          <app-icon name="mail" [size]="16" /> Send
        </button>
        <button
          mat-stroked-button
          type="button"
          data-testid="queue-bulk-delete"
          (click)="remove(selectedIds())"
        >
          <app-icon name="trash" [size]="16" /> Delete
        </button>
      } @else {
        <button mat-stroked-button type="button" (click)="restore(selectedIds())">
          <app-icon name="restore" [size]="16" /> Restore
        </button>
      }
      <button mat-button type="button" (click)="toggleAll(false)">Clear selection</button>
    </div>
  }

  <section class="dash-section" aria-label="Emails">
    <div class="sg-panel dash-panel">
      <div class="dash-table-wrap">
        @if (loading()) {
          <table class="sg-table sg-table--skeleton">
            <thead>
              <tr>
                <th>Recipient</th>
                <th>Subject</th>
                <th>Queued</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              @for (r of [1, 2, 3, 4, 5]; track r) {
                <tr>
                  <td><span class="sg-skeleton sg-skeleton--text sg-skeleton--w70"></span></td>
                  <td><span class="sg-skeleton sg-skeleton--text sg-skeleton--w80"></span></td>
                  <td><span class="sg-skeleton sg-skeleton--text sg-skeleton--w60"></span></td>
                  <td><span class="sg-skeleton sg-skeleton--chip"></span></td>
                </tr>
              }
            </tbody>
          </table>
        } @else if (error()) {
          <div class="sg-empty sg-empty--bordered">
            <app-icon name="x-circle" [size]="40" />
            <p class="sg-empty__title">Couldn't load emails</p>
            <p class="sg-empty__desc">Something went wrong on our side.</p>
            <button mat-stroked-button type="button" (click)="load()">
              <app-icon name="refresh" [size]="18" /> Try again
            </button>
          </div>
        } @else if (!rows().length) {
          <div class="sg-empty">
            @if (filtered()) {
              <app-icon name="search" [size]="40" />
              <p class="sg-empty__title">No results</p>
              <p class="sg-empty__desc">No emails here match your search.</p>
              <button mat-stroked-button type="button" (click)="clearFilters()">
                Clear filters
              </button>
            } @else {
              <app-icon name="inbox" [size]="40" />
              <p class="sg-empty__title">
                @switch (view()) {
                  @case ('HELD') {
                    Nothing waiting for approval
                  }
                  @case ('FAILED') {
                    No failed emails
                  }
                  @case ('SENT') {
                    No approved emails yet
                  }
                  @default {
                    No deleted emails
                  }
                }
              </p>
            }
          </div>
        } @else {
          <table class="sg-table">
            <thead>
              <tr>
                @if (selectable()) {
                  <th class="queue-check">
                    <mat-checkbox
                      data-testid="queue-select-all"
                      aria-label="Select all on this page"
                      [checked]="allSelected()"
                      [indeterminate]="selected().size > 0 && !allSelected()"
                      (change)="toggleAll($event.checked)"
                    />
                  </th>
                }
                <th>Recipient</th>
                <th>Subject</th>
                <th>Queued</th>
                <th>Status</th>
                <th class="sg-cell--num">Actions</th>
              </tr>
            </thead>
            <tbody>
              @for (e of rows(); track e.id) {
                <tr class="sg-table__row" [attr.data-testid]="'queue-row-' + e.id">
                  @if (selectable()) {
                    <td class="queue-check">
                      <mat-checkbox
                        [attr.aria-label]="'Select email to ' + e.to_email"
                        [checked]="selected().has(e.id)"
                        (change)="toggle(e.id, $event.checked)"
                      />
                    </td>
                  }
                  <td class="queue-to">{{ e.to_email }}</td>
                  <td class="queue-subject">
                    {{ e.subject }}
                    <p class="queue-meta">{{ e.category_display }}</p>
                    @if (e.error) {
                      <p class="queue-error">{{ e.error }}</p>
                    }
                    @if (e.approved_by) {
                      <p class="queue-meta">
                        Approved by {{ e.approved_by }}, {{ e.approved_at | date: 'medium' }}
                      </p>
                    }
                  </td>
                  <td class="aiev-muted queue-date">{{ e.created_at | date: 'medium' }}</td>
                  <td><app-status-chip [status]="e.status" /></td>
                  <td class="queue-actions">
                    <button
                      mat-icon-button
                      type="button"
                      aria-label="Preview"
                      matTooltip="Preview"
                      (click)="preview(e)"
                    >
                      <app-icon name="eye" [size]="18" />
                    </button>
                    @if (canSend()) {
                      <button
                        mat-icon-button
                        type="button"
                        aria-label="Send"
                        matTooltip="Send"
                        (click)="approve([e.id])"
                      >
                        <app-icon name="mail" [size]="18" />
                      </button>
                      <button
                        mat-icon-button
                        type="button"
                        aria-label="Delete"
                        matTooltip="Delete"
                        (click)="remove([e.id])"
                      >
                        <app-icon name="trash" [size]="18" />
                      </button>
                    }
                    @if (e.is_deleted) {
                      <button
                        mat-icon-button
                        type="button"
                        aria-label="Restore"
                        matTooltip="Restore"
                        (click)="restore([e.id])"
                      >
                        <app-icon name="restore" [size]="18" />
                      </button>
                    }
                  </td>
                </tr>
              }
            </tbody>
          </table>
        }
      </div>

      @if (!loading() && !error() && showPager()) {
        <div class="aiev-pager">
          <span class="aiev-pager__range">
            {{ rangeStart() }}–{{ rangeEnd() }} of {{ total() | number }}
          </span>
          <mat-form-field appearance="outline" class="queue-size" subscriptSizing="dynamic">
            <mat-label>Per page</mat-label>
            <mat-select [value]="pageSize()" (valueChange)="setPageSize($event)">
              @for (s of pageSizes; track s) {
                <mat-option [value]="s">{{ s }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
          <div class="aiev-pager__nav">
            <button
              mat-stroked-button
              type="button"
              (click)="goTo(page() - 1)"
              [disabled]="page() <= 1"
            >
              <app-icon name="chevron-left" [size]="16" /> Previous
            </button>
            <button
              mat-stroked-button
              type="button"
              (click)="goTo(page() + 1)"
              [disabled]="!hasNext()"
            >
              Next <app-icon name="chevron-right" [size]="16" />
            </button>
          </div>
        </div>
      }
    </div>
  </section>
</div>
